// src/frontend/src/context/AuthContext.tsx - Fixed with correct service imports
import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService, userService } from '../api/api'; // Import both authService and userService
import { User } from '../types';
import GoogleAuthService from '../services/googleAuthService';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  updateProfile: (userData: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  loginWithGoogle: async () => {},
  register: async () => {},
  logout: () => {},
  updateProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize auth state from localStorage and Firebase
  useEffect(() => {
    const initializeAuth = async () => {
      // Check localStorage first
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      if (storedUser && storedToken) {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
      }

      // Set up Firebase auth state listener
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser && !storedUser) {
          // User is signed in with Firebase but not in localStorage
          // This handles redirect results
          try {
            const result = await GoogleAuthService.getRedirectResult();
            if (result) {
              const backendUser = await GoogleAuthService.authenticateWithBackend(result.user);
              
              localStorage.setItem('user', JSON.stringify(backendUser));
              localStorage.setItem('token', backendUser.token);
              
              setUser(backendUser);
              setToken(backendUser.token);
              
              if (backendUser.isNewUser) {
                sessionStorage.setItem('showWelcome', 'true');
              }
            }
          } catch (error) {
            console.error('Firebase auth state change error:', error);
          }
        }
        setIsLoading(false);
      });

      // Clean up subscription
      return () => unsubscribe();
    };

    initializeAuth();
  }, []);

  // 🔧 FIXED: Use authService.login
  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });
      
      // Extract the user data from the response
      const userData = response.data.data || response.data;
      
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', userData.token);

      setUser(userData);
      setToken(userData.token);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Google login
  const loginWithGoogle = async () => {
    try {
      setIsLoading(true);
      
      // Use popup on desktop, redirect on mobile
      const usePopup = !GoogleAuthService.isMobileDevice();
      
      const result = await GoogleAuthService.completeGoogleAuth(usePopup);
      
      if (result) {
        // Authentication completed
        localStorage.setItem('user', JSON.stringify(result));
        localStorage.setItem('token', result.token);
        
        setUser(result);
        setToken(result.token);
        
        // Set welcome flag for new users
        if (result.isNewUser) {
          sessionStorage.setItem('showWelcome', 'true');
        }
      }
      // If result is null, redirect is in progress (mobile)
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 🔧 FIXED: Use authService.register
  const register = async (userData: any) => {
    try {
      const response = await authService.register(userData);
      
      // Extract the user data from the response
      const newUser = response.data.data || response.data;

      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('token', newUser.token);

      setUser(newUser);
      setToken(newUser.token);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setToken(null);
    
    // Sign out from Firebase as well
    auth.signOut().catch(console.error);
  };

  // Update profile function - keep using userService for profile operations
  const updateProfile = async (userData: any) => {
    try {
      const response = await userService.updateProfile(userData);
      const updatedUser = response.data.data || response.data;

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        login,
        loginWithGoogle,
        register,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;